import React, { useState, useEffect, useCallback } from 'react';
import {
  Users, UserCircle, Calendar, Plus, Edit2, Trash2, Save, X,
  ChevronDown, ChevronRight, Clock, Briefcase, AlertTriangle,
  CheckCircle, Settings, Loader2, RefreshCw, Bot
} from 'lucide-react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { PageErrorBoundary, WidgetErrorBoundary } from '../components/ErrorBoundary';
import { SkeletonCard } from '../components/Skeleton';
import EmptyState from '../components/EmptyState';
import BackButton from '../components/BackButton';

// ==========================================
// AI ADVISOR TOGGLE (COMING SOON)
// ==========================================
const AIAdvisorToggle = () => (
  <div 
    className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-lg opacity-60 cursor-not-allowed"
    data-testid="ai-advisor-toggle"
    title="AI Resource Advisor - Coming Soon"
  >
    <Bot className="w-4 h-4 text-gray-400" />
    <span className="text-xs text-gray-500">AI Advisor</span>
    <span className="text-[10px] bg-gray-200 text-gray-500 px-1.5 py-0.5 rounded-full">Soon</span>
  </div>
);

// ==========================================
// CONSTANTS
// ==========================================

const WORKING_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const DEFAULT_HOLIDAYS = [
  { date: '2026-01-01', name: 'New Year' },
  { date: '2026-03-21', name: 'Human Rights Day' },
  { date: '2026-04-10', name: 'Good Friday' },
  { date: '2026-04-27', name: 'Freedom Day' },
  { date: '2026-05-01', name: 'Workers Day' },
  { date: '2026-06-16', name: 'Youth Day' },
  { date: '2026-08-09', name: "Women's Day" },
  { date: '2026-09-24', name: 'Heritage Day' },
  { date: '2026-12-16', name: 'Day of Reconciliation' },
  { date: '2026-12-25', name: 'Christmas' },
  { date: '2026-12-26', name: 'Day of Goodwill' },
];

// ==========================================
// CALENDAR EDITOR MODAL
// ==========================================

const CalendarEditorModal = ({ entity, entityType, onClose, onSave, tenantId }) => {
  const [calendar, setCalendar] = useState({
    working_days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
    holidays: [],
    weekly_hours: 40,
    monthly_overrides: {}
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newHoliday, setNewHoliday] = useState('');
  const [overrideMonth, setOverrideMonth] = useState('');
  const [overrideHours, setOverrideHours] = useState('');

  useEffect(() => {
    loadCalendar();
  }, [entity.id]);

  const loadCalendar = async () => {
    try {
      const res = await api.getCapacityCalendar(entityType, entity.id, tenantId);
      if (res.data.calendar) {
        setCalendar(res.data.calendar);
      }
    } catch (err) {
      console.error('Failed to load calendar:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateCapacityCalendar(entityType, entity.id, tenantId, calendar);
      toast.success('Calendar updated');
      onSave();
      onClose();
    } catch (err) {
      toast.error('Failed to update calendar');
    } finally {
      setSaving(false);
    }
  };

  const toggleWorkingDay = (day) => {
    setCalendar(prev => ({
      ...prev,
      working_days: prev.working_days.includes(day)
        ? prev.working_days.filter(d => d !== day)
        : [...prev.working_days, day]
    }));
  };

  const addHoliday = () => {
    if (newHoliday && !calendar.holidays.includes(newHoliday)) {
      setCalendar(prev => ({
        ...prev,
        holidays: [...prev.holidays, newHoliday].sort()
      }));
      setNewHoliday('');
    }
  };

  const removeHoliday = (holiday) => {
    setCalendar(prev => ({
      ...prev,
      holidays: prev.holidays.filter(h => h !== holiday)
    }));
  };

  const addOverride = () => {
    if (overrideMonth && overrideHours) {
      setCalendar(prev => ({
        ...prev,
        monthly_overrides: {
          ...prev.monthly_overrides,
          [overrideMonth]: parseFloat(overrideHours)
        }
      }));
      setOverrideMonth('');
      setOverrideHours('');
    }
  };

  const removeOverride = (month) => {
    setCalendar(prev => {
      const { [month]: _, ...rest } = prev.monthly_overrides;
      return { ...prev, monthly_overrides: rest };
    });
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-8">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden" data-testid="calendar-editor-modal">
        {/* Header */}
        <div className="px-6 py-4 border-b bg-gradient-to-r from-purple-600 to-indigo-600">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Calendar className="w-6 h-6 text-white" />
              <div>
                <h2 className="text-lg font-semibold text-white">Edit Capacity Calendar</h2>
                <p className="text-sm text-purple-200">{entity.name}</p>
              </div>
            </div>
            <button onClick={onClose} className="text-white/80 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)] space-y-6">
          {/* Working Days */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Working Days</label>
            <div className="flex gap-2 flex-wrap">
              {WORKING_DAYS.map(day => (
                <button
                  key={day}
                  onClick={() => toggleWorkingDay(day)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    calendar.working_days.includes(day)
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {day}
                </button>
              ))}
            </div>
          </div>

          {/* Weekly Hours */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Weekly Hours</label>
            <input
              type="number"
              value={calendar.weekly_hours}
              onChange={(e) => setCalendar(prev => ({ ...prev, weekly_hours: parseFloat(e.target.value) || 40 }))}
              className="w-32 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              min="0"
              max="168"
            />
            <span className="ml-2 text-sm text-gray-500">hours/week</span>
          </div>

          {/* Holidays */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Holidays</label>
            <div className="flex gap-2 mb-3">
              <input
                type="date"
                value={newHoliday}
                onChange={(e) => setNewHoliday(e.target.value)}
                className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
              <button
                onClick={addHoliday}
                disabled={!newHoliday}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {calendar.holidays.map(holiday => (
                <span
                  key={holiday}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm"
                >
                  {holiday}
                  <button onClick={() => removeHoliday(holiday)} className="hover:text-red-900">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              {calendar.holidays.length === 0 && (
                <span className="text-sm text-gray-400">No holidays configured</span>
              )}
            </div>
            <div className="mt-2">
              <button
                onClick={() => setCalendar(prev => ({
                  ...prev,
                  holidays: [...new Set([...prev.holidays, ...DEFAULT_HOLIDAYS.map(h => h.date)])].sort()
                }))}
                className="text-sm text-purple-600 hover:text-purple-800"
              >
                + Add standard SA public holidays
              </button>
            </div>
          </div>

          {/* Monthly Overrides */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Monthly Capacity Overrides</label>
            <p className="text-xs text-gray-500 mb-3">Override the default monthly capacity for specific months (e.g., December holiday period)</p>
            <div className="flex gap-2 mb-3">
              <input
                type="month"
                value={overrideMonth}
                onChange={(e) => setOverrideMonth(e.target.value)}
                className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
              <input
                type="number"
                placeholder="Hours"
                value={overrideHours}
                onChange={(e) => setOverrideHours(e.target.value)}
                className="w-24 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
              <button
                onClick={addOverride}
                disabled={!overrideMonth || !overrideHours}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-2">
              {Object.entries(calendar.monthly_overrides || {}).map(([month, hours]) => (
                <div key={month} className="flex items-center justify-between px-3 py-2 bg-amber-50 rounded-lg">
                  <span className="text-sm">
                    <span className="font-medium">{month}</span>: {hours} hours
                  </span>
                  <button onClick={() => removeOverride(month)} className="text-amber-600 hover:text-amber-800">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {Object.keys(calendar.monthly_overrides || {}).length === 0 && (
                <span className="text-sm text-gray-400">No overrides configured</span>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

// ==========================================
// ROLE/TEAM EDITOR MODAL
// ==========================================

const EntityEditorModal = ({ entity, entityType, onClose, onSave, tenantId }) => {
  const [formData, setFormData] = useState({
    name: entity?.name || '',
    description: entity?.description || '',
    default_weekly_hours: entity?.default_weekly_hours || 40,
    default_rate: entity?.default_rate || 100,
    skills: entity?.skills || [],
    lead: entity?.lead || '',
    members: entity?.members || []
  });
  const [saving, setSaving] = useState(false);
  const [newSkill, setNewSkill] = useState('');
  const [newMember, setNewMember] = useState({ name: '', role: '' });

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    setSaving(true);
    try {
      if (entity?.id) {
        // Update
        if (entityType === 'role') {
          await api.updateRole(entity.id, tenantId, formData);
        } else {
          await api.updateResourceTeam(entity.id, tenantId, formData);
        }
        toast.success(`${entityType === 'role' ? 'Role' : 'Team'} updated`);
      } else {
        // Create
        const payload = { ...formData, tenant_id: tenantId };
        if (entityType === 'role') {
          await api.createRole(payload);
        } else {
          await api.createResourceTeam(payload);
        }
        toast.success(`${entityType === 'role' ? 'Role' : 'Team'} created`);
      }
      onSave();
      onClose();
    } catch (err) {
      toast.error(`Failed to save ${entityType}`);
    } finally {
      setSaving(false);
    }
  };

  const addSkill = () => {
    if (newSkill && !formData.skills.includes(newSkill)) {
      setFormData(prev => ({ ...prev, skills: [...prev.skills, newSkill] }));
      setNewSkill('');
    }
  };

  const removeSkill = (skill) => {
    setFormData(prev => ({ ...prev, skills: prev.skills.filter(s => s !== skill) }));
  };

  const addMember = () => {
    if (newMember.name) {
      setFormData(prev => ({ ...prev, members: [...prev.members, { ...newMember }] }));
      setNewMember({ name: '', role: '' });
    }
  };

  const removeMember = (index) => {
    setFormData(prev => ({ ...prev, members: prev.members.filter((_, i) => i !== index) }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden" data-testid="entity-editor-modal">
        {/* Header */}
        <div className="px-6 py-4 border-b bg-gradient-to-r from-purple-600 to-indigo-600">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {entityType === 'role' ? <UserCircle className="w-6 h-6 text-white" /> : <Users className="w-6 h-6 text-white" />}
              <h2 className="text-lg font-semibold text-white">
                {entity?.id ? 'Edit' : 'Create'} {entityType === 'role' ? 'Role' : 'Team'}
              </h2>
            </div>
            <button onClick={onClose} className="text-white/80 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)] space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              placeholder={entityType === 'role' ? 'e.g., Senior Developer' : 'e.g., Platform Team'}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              rows={2}
            />
          </div>

          {entityType === 'role' && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Weekly Hours</label>
                  <input
                    type="number"
                    value={formData.default_weekly_hours}
                    onChange={(e) => setFormData(prev => ({ ...prev, default_weekly_hours: parseFloat(e.target.value) || 40 }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Default Rate ($/hr)</label>
                  <input
                    type="number"
                    value={formData.default_rate}
                    onChange={(e) => setFormData(prev => ({ ...prev, default_rate: parseFloat(e.target.value) || 100 }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Skills</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newSkill}
                    onChange={(e) => setNewSkill(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addSkill()}
                    className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="Add skill..."
                  />
                  <button onClick={addSkill} className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.skills.map(skill => (
                    <span key={skill} className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">
                      {skill}
                      <button onClick={() => removeSkill(skill)}><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
              </div>
            </>
          )}

          {entityType === 'team' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Team Lead</label>
                <input
                  type="text"
                  value={formData.lead}
                  onChange={(e) => setFormData(prev => ({ ...prev, lead: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="Team lead name..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Members</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newMember.name}
                    onChange={(e) => setNewMember(prev => ({ ...prev, name: e.target.value }))}
                    className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="Member name..."
                  />
                  <input
                    type="text"
                    value={newMember.role}
                    onChange={(e) => setNewMember(prev => ({ ...prev, role: e.target.value }))}
                    className="w-32 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="Role..."
                  />
                  <button onClick={addMember} className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <div className="space-y-2">
                  {formData.members.map((member, idx) => (
                    <div key={idx} className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg">
                      <span className="text-sm">
                        <span className="font-medium">{member.name}</span>
                        {member.role && <span className="text-gray-500 ml-2">({member.role})</span>}
                      </span>
                      <button onClick={() => removeMember(idx)} className="text-gray-400 hover:text-red-600">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {entity?.id ? 'Save Changes' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ==========================================
// CAPACITY TABLE COMPONENT
// ==========================================

const CapacityTable = ({ entities, entityType, onEditCalendar, onEdit, onDelete }) => {
  const [expandedId, setExpandedId] = useState(null);

  if (entities.length === 0) {
    return (
      <EmptyState
        icon={entityType === 'role' ? UserCircle : Users}
        title={`No ${entityType}s configured`}
        description={`Create your first ${entityType} to start capacity planning.`}
      />
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b bg-gray-50">
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Name</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Weekly Hours</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Utilisation</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
            <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
          </tr>
        </thead>
        <tbody>
          {entities.map(entity => {
            const utilisation = entity.utilisation?.utilisation_percent || 0;
            const statusColor = utilisation > 100 ? 'red' : utilisation >= 80 ? 'amber' : utilisation >= 50 ? 'blue' : 'green';
            
            return (
              <React.Fragment key={entity.id}>
                <tr className="border-b hover:bg-gray-50" data-testid={`capacity-row-${entity.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setExpandedId(expandedId === entity.id ? null : entity.id)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        {expandedId === entity.id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </button>
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        entityType === 'role' ? 'bg-purple-100' : 'bg-blue-100'
                      }`}>
                        {entityType === 'role' 
                          ? <UserCircle className="w-4 h-4 text-purple-600" />
                          : <Users className="w-4 h-4 text-blue-600" />
                        }
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{entity.name}</p>
                        <p className="text-xs text-gray-500">{entity.description || 'No description'}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm font-medium">{entity.default_weekly_hours || 40}h</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full ${
                            statusColor === 'red' ? 'bg-red-500' :
                            statusColor === 'amber' ? 'bg-amber-500' :
                            statusColor === 'blue' ? 'bg-blue-500' : 'bg-green-500'
                          }`}
                          style={{ width: `${Math.min(utilisation, 100)}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium">{utilisation}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                      statusColor === 'red' ? 'bg-red-100 text-red-700' :
                      statusColor === 'amber' ? 'bg-amber-100 text-amber-700' :
                      statusColor === 'blue' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                    }`}>
                      {statusColor === 'red' && <AlertTriangle className="w-3 h-3" />}
                      {statusColor === 'amber' && <Clock className="w-3 h-3" />}
                      {statusColor === 'green' && <CheckCircle className="w-3 h-3" />}
                      {utilisation > 100 ? 'Over Capacity' : 
                       utilisation >= 80 ? 'Near Capacity' : 
                       utilisation >= 50 ? 'Normal' : 'Under-utilized'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => onEditCalendar(entity)}
                        className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg"
                        title="Edit Calendar"
                      >
                        <Calendar className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => onEdit(entity)}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => onDelete(entity)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
                {expandedId === entity.id && (
                  <tr className="bg-gray-50">
                    <td colSpan={5} className="px-4 py-4">
                      <div className="ml-12 grid grid-cols-4 gap-4">
                        <div className="bg-white p-3 rounded-lg border">
                          <p className="text-xs text-gray-500">Capacity</p>
                          <p className="text-lg font-semibold">{entity.utilisation?.capacity_hours?.toFixed(0) || 173}h</p>
                        </div>
                        <div className="bg-white p-3 rounded-lg border">
                          <p className="text-xs text-gray-500">Allocated</p>
                          <p className="text-lg font-semibold">{entity.utilisation?.allocated_hours?.toFixed(0) || 0}h</p>
                        </div>
                        <div className="bg-white p-3 rounded-lg border">
                          <p className="text-xs text-gray-500">Remaining</p>
                          <p className="text-lg font-semibold">{entity.utilisation?.remaining_hours?.toFixed(0) || 173}h</p>
                        </div>
                        {entityType === 'role' && (
                          <div className="bg-white p-3 rounded-lg border">
                            <p className="text-xs text-gray-500">Rate</p>
                            <p className="text-lg font-semibold">${entity.default_rate || 100}/hr</p>
                          </div>
                        )}
                        {entityType === 'team' && (
                          <div className="bg-white p-3 rounded-lg border">
                            <p className="text-xs text-gray-500">Members</p>
                            <p className="text-lg font-semibold">{entity.member_count || 0}</p>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ==========================================
// MAIN PAGE COMPONENT
// ==========================================

const ResourceCapacityPage = () => {
  const { tenantId: authTenantId } = useAuth();
  // TENANT ISOLATION: Use impersonated tenant if sysadmin is impersonating
  const tenantId = localStorage.getItem('impersonated_tenant_id') || authTenantId;
  const [activeTab, setActiveTab] = useState('roles');
  const [roles, setRoles] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCalendarEditor, setShowCalendarEditor] = useState(null);
  const [showEntityEditor, setShowEntityEditor] = useState(null);
  const [entityToEdit, setEntityToEdit] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [rolesRes, teamsRes] = await Promise.all([
        api.listRoles(tenantId, true),
        api.listResourceTeams(tenantId, true)
      ]);
      setRoles(rolesRes.data.roles || []);
      setTeams(teamsRes.data.teams || []);
    } catch (err) {
      console.error('Failed to load data:', err);
      toast.error('Failed to load resource data');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDelete = async (entity, type) => {
    if (!window.confirm(`Are you sure you want to delete "${entity.name}"?`)) return;
    
    try {
      if (type === 'role') {
        await api.deleteRole(entity.id, tenantId);
      } else {
        await api.deleteResourceTeam(entity.id, tenantId);
      }
      toast.success(`${type === 'role' ? 'Role' : 'Team'} deleted`);
      loadData();
    } catch (err) {
      toast.error(`Failed to delete ${type}`);
    }
  };

  return (
    <PageErrorBoundary>
      <div className="min-h-screen bg-gray-50 p-6" data-testid="resource-capacity-page">
        {/* Back Button */}
        <BackButton to="/resources" label="Back to Resources" className="mb-4" />

        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Resource Capacity</h1>
            <p className="text-gray-500">Configure roles, teams, and their availability calendars</p>
          </div>
          <AIAdvisorToggle />
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
          <div className="border-b px-4">
            <div className="flex items-center justify-between">
              <div className="flex gap-1">
                <button
                  onClick={() => setActiveTab('roles')}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'roles'
                      ? 'border-purple-600 text-purple-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                  data-testid="tab-roles"
                >
                  <UserCircle className="w-4 h-4 inline mr-2" />
                  Roles ({roles.length})
                </button>
                <button
                  onClick={() => setActiveTab('teams')}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'teams'
                      ? 'border-purple-600 text-purple-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                  data-testid="tab-teams"
                >
                  <Users className="w-4 h-4 inline mr-2" />
                  Teams ({teams.length})
                </button>
              </div>
              <div className="flex items-center gap-2 py-2">
                <button
                  onClick={loadData}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                  title="Refresh"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
                <button
                  onClick={() => {
                    setEntityToEdit(null);
                    setShowEntityEditor(activeTab === 'roles' ? 'role' : 'team');
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                  data-testid="add-entity-btn"
                >
                  <Plus className="w-4 h-4" />
                  Add {activeTab === 'roles' ? 'Role' : 'Team'}
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          <WidgetErrorBoundary widgetName="Capacity Table">
            {loading ? (
              <div className="p-6 space-y-4">
                <SkeletonCard />
                <SkeletonCard />
                <SkeletonCard />
              </div>
            ) : (
              <CapacityTable
                entities={activeTab === 'roles' ? roles : teams}
                entityType={activeTab === 'roles' ? 'role' : 'team'}
                onEditCalendar={(entity) => setShowCalendarEditor(entity)}
                onEdit={(entity) => {
                  setEntityToEdit(entity);
                  setShowEntityEditor(activeTab === 'roles' ? 'role' : 'team');
                }}
                onDelete={(entity) => handleDelete(entity, activeTab === 'roles' ? 'role' : 'team')}
              />
            )}
          </WidgetErrorBoundary>
        </div>

        {/* Calendar Editor Modal */}
        {showCalendarEditor && (
          <CalendarEditorModal
            entity={showCalendarEditor}
            entityType={activeTab === 'roles' ? 'role' : 'team'}
            tenantId={tenantId}
            onClose={() => setShowCalendarEditor(null)}
            onSave={loadData}
          />
        )}

        {/* Entity Editor Modal */}
        {showEntityEditor && (
          <EntityEditorModal
            entity={entityToEdit}
            entityType={showEntityEditor}
            tenantId={tenantId}
            onClose={() => {
              setShowEntityEditor(null);
              setEntityToEdit(null);
            }}
            onSave={loadData}
          />
        )}
      </div>
    </PageErrorBoundary>
  );
};

export default ResourceCapacityPage;
